import { useIntl } from 'react-intl'
import { useState, useMemo, useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import OfferDescriptionLoadingSkeleton from 'components/Common/Loading/OfferDescriptionLoadingSkeleton'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'

const DescriptionContainer = styled.div`
  padding: ${rem(8)} ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const DescriptionHeader = styled(Group)`
  margin-bottom: ${rem(4)};
  font-weight: bold;
  font-size: ${rem(14)};
  font-family: ${props => props.theme.font.primary.family};
`

const OfferDescriptionHeader = styled.span`
  background: ${props => props.theme.palette.product.ai.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const StyledList = styled.ul`
  margin: 0;
  padding-left: ${rem(12)};
`

const ToggleHint = styled.span`
  margin-left: ${rem(4)};
  color: ${props => props.theme.palette.brand.primary.normal};
  text-decoration: underline;
`

const PlainText = styled.span`
  display: inline;
`

interface Props {
  description?: Array<string>
  isLoading?: boolean
  className?: string
  position?: number
  isError?: boolean
}

function OfferDescription({ description, isLoading = false, className, position }: Props) {
  const intl = useIntl()
  const [isExpanded, setIsExpanded] = useState(false)
  const displayPosition = position !== undefined ? position + 1 : ''

  const showLessLabel = intl.formatMessage({
    id: 'zCjrQH89LisG3yZXhGI0',
    defaultMessage: 'Show less',
    description: 'Toggle label to collapse the AI offer description in the offer listing section',
  })
  const showMoreLabel = intl.formatMessage({
    id: 'TyBN8pq5GqCn5ViXprPo',
    defaultMessage: 'Show more',
    description: 'Toggle label to expand the AI offer description in the offer listing section',
  })
  const bestMatchLabel = intl.formatMessage({
    id: '0Rd69Piwgin6GV6BuMjd',
    defaultMessage: 'Best match',
    description: 'Section heading for the top AI-recommended offer match',
  })
  const matchNumberLabel = intl.formatMessage({
    id: 'WRag4IPFMivpniIxSOek',
    defaultMessage: 'Match #{displayPosition}',
    description: 'Heading for an AI-recommended offer match showing its rank position',
  }, { displayPosition })

  const lines = useMemo(() => description || [], [description])
  const hasMultipleLines = lines.length > 1

  const toggleExpanded = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    setIsExpanded(previous => !previous)
  }, [])

  const bulletListItems = useMemo(
    () =>
      lines.map((line, index) => (
        <li key={`${line}-${index}`}>
          {line}
        </li>
      )),
    [lines],
  )

  const primaryLine = useMemo(() => lines[0] || '', [lines])

  const renderDescriptionContent = useMemo(() => {
    if (!hasMultipleLines) {
      return <PlainText>{primaryLine}</PlainText>
    }

    if (isExpanded) {
      return (
        <>
          <StyledList>{bulletListItems}</StyledList>
          <ToggleHint>{showLessLabel}</ToggleHint>
        </>
      )
    }

    return (
      <>
        <PlainText>{primaryLine}</PlainText>
        <ToggleHint>{showMoreLabel}</ToggleHint>
      </>
    )
  }, [bulletListItems, hasMultipleLines, isExpanded, primaryLine, showLessLabel, showMoreLabel])

  if (isLoading) {
    return <OfferDescriptionLoadingSkeleton position={position} className={className} />
  }

  if (!description || description.length === 0) {
    return null
  }

  return (
    <DescriptionContainer className={className}>
      <DescriptionHeader direction="horizontal" verticalAlign="center" gap={8}>
        <AISparkleIcon size="S" />
        <OfferDescriptionHeader>
          {displayPosition === 1 ? bestMatchLabel : matchNumberLabel}
        </OfferDescriptionHeader>
      </DescriptionHeader>
      <BodyText
        variant="medium"
        colour="neutral-two"
        onClick={hasMultipleLines ? toggleExpanded : undefined}
      >
        {renderDescriptionContent}
      </BodyText>
    </DescriptionContainer>
  )
}

export default OfferDescription
